import { forwardRef, useMemo } from "react";
import { Chart, useChartTheme, resolveChartColor, usePrefersReducedMotion, commonStyles, commonTooltip, commonLegend, type ChartHandle, type ChartProps } from "./Chart";

// Stage 6: local replacement for @datasirpi/ui's <PieChart>. Ported
// letter-for-letter from the vendor's compiled bundle, incl. the
// all-zero-values / empty-data placeholder-slice handling and the
// donut center-label rich-text formatting.
export interface PieSlice {
  name: string;
  value: number;
  color?: string;
}

export interface PieChartProps extends Omit<ChartProps, "option" | "tabularData"> {
  data: readonly PieSlice[];
  innerRadius?: string | number;
  outerRadius?: string | number;
  showLegend?: boolean;
  showTooltip?: boolean;
  centerLabel?: { title: string; value: string };
  tableCaption?: string;
  /** d2d-DetectHub eval 2.3: at most this many named slices; the rest are
      summed into one neutral "Other". A pie past five slices is unreadable,
      and the sixth palette slot is where colours start to repeat. 0 = no cap. */
  maxSlices?: number;
}

/** The `max` largest slices, in their original order, plus one "Other" for the rest. */
function capSlices(data: readonly PieSlice[], max: number, otherColor: string): readonly PieSlice[] {
  if (!max || data.length <= max) return data;
  const keep = new Set(
    data
      .map((d, i) => ({ v: d.value, i }))
      .sort((a, b) => b.v - a.v)
      .slice(0, max)
      .map((x) => x.i)
  );
  const rest = data.reduce((sum, d, i) => (keep.has(i) ? sum : sum + (d.value || 0)), 0);
  return [...data.filter((_, i) => keep.has(i)), { name: "Other", value: rest, color: otherColor }];
}

export const PieChart = forwardRef<ChartHandle, PieChartProps>(function PieChart(
  { data: rawData, innerRadius, outerRadius = "70%", showLegend = true, showTooltip = true, centerLabel, tableCaption, maxSlices = 5, ...rest },
  ref
) {
  const theme = useChartTheme();
  const reducedMotion = usePrefersReducedMotion();
  // "Other" is neutral: it is not a category, so it takes no palette hue.
  const data = useMemo(() => capSlices(rawData, maxSlices, theme.textMuted), [rawData, maxSlices, theme.textMuted]);

  const option = useMemo(() => {
    const radius = innerRadius != null ? [innerRadius, outerRadius] : outerRadius;
    const isEmpty = !data || data.length === 0;
    const isAllZero = !isEmpty && data.every((d) => !d.value);
    let chartData;
    if (isEmpty) {
      chartData = [
        {
          name: "No Data",
          value: 1,
          actualValue: 0,
          itemStyle: { color: theme.gridLine || "#e4e4e7", borderColor: theme.bg, borderWidth: 2 },
          emphasis: { scale: false, itemStyle: { color: theme.gridLine || "#e4e4e7", borderColor: theme.bg, borderWidth: 2 } },
        },
      ];
    } else {
      chartData = data.map((d, i) => {
        const color = resolveChartColor(d.color, theme.palette[i % theme.palette.length]);
        return {
          name: d.name,
          value: isAllZero ? 1 : d.value,
          ...(isAllZero ? { actualValue: 0 } : {}),
          itemStyle: { color, borderColor: theme.bg, borderWidth: 2 },
          emphasis: {
            scale: true,
            scaleSize: 6,
            itemStyle: { color, borderColor: theme.bg, borderWidth: 2, shadowBlur: 10, shadowOffsetX: 0, shadowColor: "rgba(0, 0, 0, 0.15)" },
          },
        };
      });
    }
    return {
      ...commonStyles(theme, { reducedMotion }),
      tooltip: showTooltip
        ? {
            trigger: "item",
            ...commonTooltip(theme),
            formatter: (params: { data?: { actualValue?: number }; value?: number; marker?: unknown; name: string; percent?: number }) => {
              const d = params.data;
              const val = d?.actualValue !== undefined ? d.actualValue : params.value;
              const marker = typeof params.marker === "string" ? params.marker : "";
              const percent = typeof params.percent === "number" && !isAllZero && !isEmpty ? ` (${params.percent}%)` : "";
              return `${marker}${params.name}: <strong>${val}</strong>${percent}`;
            },
          }
        : { show: false },
      legend: showLegend && !isEmpty ? { ...commonLegend(theme), orient: "horizontal" } : { show: false },
      series: [
        {
          type: "pie",
          radius,
          center: ["50%", showLegend && !isEmpty ? "45%" : "50%"],
          avoidLabelOverlap: true,
          emphasis: { scale: !isEmpty },
          label: centerLabel
            ? {
                show: true,
                position: "center",
                fontFamily: theme.fontFamily,
                formatter: () => `{title|${centerLabel.title}}\n{value|${centerLabel.value}}`,
                rich: {
                  title: { color: theme.textMuted, fontSize: 12, lineHeight: 18 },
                  value: { color: theme.textColor, fontSize: 20, fontWeight: "bold" },
                },
              }
            : isEmpty && innerRadius != null
              ? {
                  show: true,
                  position: "center",
                  fontFamily: theme.fontFamily,
                  formatter: () => "{title|No Data}\n{value|0}",
                  rich: {
                    title: { color: theme.textMuted, fontSize: 12, lineHeight: 18 },
                    value: { color: theme.textColor, fontSize: 20, fontWeight: "bold" },
                  },
                }
              : { color: theme.textColor, fontFamily: theme.fontFamily },
          data: chartData,
        },
      ],
    };
  }, [data, innerRadius, outerRadius, showLegend, showTooltip, centerLabel, theme, reducedMotion]);

  const tabularData = useMemo(
    () => ({
      ...(tableCaption ? { caption: tableCaption } : {}),
      headers: ["category", "value"],
      rows: data.map((d) => [d.name, String(d.value)]),
    }),
    [data, tableCaption]
  );

  return <Chart ref={ref} option={option} tabularData={tabularData} {...rest} />;
});
